import React, { useEffect } from 'react'
import { Button, Grid } from '@mui/material'
import { object, string } from 'yup'
import { useFormContext } from 'react-hook-form'

import { Dialog } from '../dialog.tsx'
import { Form, FormSchema, FormSchemaMessages, SubmitButton, TextFieldControl } from '../form'
import { preventHandler } from '../../utils.ts'
import { RESEND_ENDPOINTS, useMutateAqcRefund } from '../../services'
import { useSnackbarContext } from '../snackbar'
import { AcqPayment, RefundOperationParams } from '../../types.ts'
import { useStages } from '../../hooks'
import { FadeInWrapper } from '../fade-in-wrapper.tsx'
import { ConfirmRefundStage } from '../refund-sbp-button'

import { RefundFormState } from './refund-button.types.ts'

type RefundAcqFormState = Omit<RefundOperationParams, 'reverseSum'>
const refundFormSchema = (isVerificationStage = false) =>
  object<FormSchema<RefundAcqFormState>>({
    emailCode: isVerificationStage ? string().required(FormSchemaMessages.required) : string(),
  })

export interface RefundDialogProps extends Pick<AcqPayment, 'id'> {
  onClose(): void
}

export const RefundAcqDialog = ({ id, onClose }: RefundDialogProps): React.ReactElement => {
  const { stage, handleNextState } = useStages()
  const { addSnack } = useSnackbarContext()
  const handleRefund = useMutateAqcRefund(id.toString())

  const handleRefundInit = async (formValues: RefundAcqFormState) => {
    await preventHandler(handleRefund.mutateAsync, handleRefund.isPending)(formValues)
  }

  useEffect(() => {
    switch (handleRefund.status) {
      case 'success': {
        const response = handleRefund.data?.data
        const isSuccess = response?.state === 'OK'

        addSnack({
          message: isSuccess
            ? response.message ?? 'Возврат платежа в процессе'
            : response.message ?? 'Произошла ошибка при создании возврата, попробуйте ещё раз',
          severity: isSuccess ? 'success' : 'error',
          timeout: isSuccess ? 6000 : 3000,
          closeIcon: false,
        })
        if (isSuccess) {
          onClose()
          handleRefund.reset()
        }
        break
      }
      case 'error': {
        // бэк не выбрасывает ошибку если ввести возврат больше доступной суммы
        addSnack({
          message: 'Произошла непредвиденная ошибка, попробуйте ещё раз',
          severity: 'error',
          timeout: 3000,
          closeIcon: false,
        })
        handleRefund.reset()
        break
      }
      default:
        break
    }
  }, [handleRefund.status])

  const isVerificationStage = stage === 1

  return (
    <Form<RefundFormState>
      name="refundOperation"
      onSubmit={handleRefundInit}
      validationSchema={refundFormSchema(isVerificationStage)}
    >
      <Dialog
        open
        maxWidth="lg"
        content={
          <>
            {stage === 0 && (
              <FadeInWrapper>
                <Grid container flexDirection="column" spacing={2}>
                  <Grid item>
                    <TextFieldControl<RefundFormState>
                      name="purpose"
                      fullWidth
                      label="Назначение возврата"
                      size="small"
                    />
                  </Grid>
                </Grid>
              </FadeInWrapper>
            )}
            {isVerificationStage && <ConfirmRefundStage resendCodeEndpoint={RESEND_ENDPOINTS.resendAqcCode} />}
          </>
        }
        actions={
          <>
            {stage === 0 && (
              <>
                <Button fullWidth onClick={onClose} disabled={handleRefund.isPending} size="large" color="secondary">
                  Отмена
                </Button>
                <RefundVerificationCodeButton handleNextStage={handleNextState} />
              </>
            )}
            {isVerificationStage && <CreateRefundButton />}
          </>
        }
        onClose={onClose}
        title={`Возврат по операции №${id}`}
      />
    </Form>
  )
}

const RefundVerificationCodeButton = ({ handleNextStage }: { handleNextStage(): void }): React.ReactElement => {
  const { formState } = useFormContext()
  const { isValid } = formState

  return (
    <Button fullWidth size="large" disabled={!isValid} onClick={handleNextStage}>
      Продолжить
    </Button>
  )
}

const CreateRefundButton = (): React.ReactElement => {
  const { formState } = useFormContext()
  const { isValid } = formState

  return (
    <SubmitButton form="refundOperation" disabled={!isValid}>
      Сделать возврат
    </SubmitButton>
  )
}
